// src/modules/nlp/EmbeddingsLab.jsx
// Word Embeddings Lab — Co-occurrence · Word2Vec · GloVe · FastText · Analogies · 2D Projection · Similarity Matrix
// Tabs: input | cooccur | project | similarity | analogies | training | models | concepts

import React, { useState, useCallback } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  BarChart, Bar, Legend, LineChart, Line, AreaChart, Area,
} from 'recharts';
import {
  tokenizeWords, removeStopWords, buildCooccurrence, project2D,
  cosineSimilarity, SAMPLE_TEXTS,
} from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = '#f472b6';
const P = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399','#60a5fa','#a78bfa'];
const TABS = ['input','cooccur','project','similarity','analogies','training','models','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);

// Cosine similarity between two co-occurrence matrix rows
function rowSimilarity(mat, vocab, w1, w2) {
  const i1 = vocab.indexOf(w1), i2 = vocab.indexOf(w2);
  if (i1<0||i2<0) return null;
  const v1 = mat[i1], v2 = mat[i2];
  const dot  = v1.reduce((s,v,k)=>s+v*v2[k],0);
  const m1   = Math.sqrt(v1.reduce((s,v)=>s+v*v,0));
  const m2   = Math.sqrt(v2.reduce((s,v)=>s+v*v,0));
  return m1&&m2 ? +(dot/(m1*m2)).toFixed(4) : 0;
}

// Pretend Word2Vec analogy using co-occurrence projection
function solveAnalogy(points, a, b, c) {
  // a:b :: c:? → find word closest to vec(b) - vec(a) + vec(c)
  const pa = points.find(p=>p.word===a);
  const pb = points.find(p=>p.word===b);
  const pc = points.find(p=>p.word===c);
  if (!pa||!pb||!pc) return null;
  const tx = pb.x - pa.x + pc.x;
  const ty = pb.y - pa.y + pc.y;
  const candidates = points.filter(p=>p.word!==a&&p.word!==b&&p.word!==c)
    .map(p=>({ word:p.word, dist:Math.sqrt((p.x-tx)**2+(p.y-ty)**2) }))
    .sort((a,b)=>a.dist-b.dist);
  return candidates.slice(0,5);
}

export default function EmbeddingsLab() {
  const [tab, setTab]         = useState('input');
  const [text, setText]       = useState(Object.values(SAMPLE_TEXTS).join(' '));
  const [windowSize, setWindowSize] = useState(3);
  const [cooccur, setCooccur] = useState(null);
  const [points2D, setPoints2D]     = useState([]);
  const [selectedWord, setSelectedWord] = useState('');
  const [simWord1, setSimWord1] = useState('');
  const [simWord2, setSimWord2] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [analogyA, setAnalogyA] = useState('');
  const [analogyB, setAnalogyB] = useState('');
  const [analogyC, setAnalogyC] = useState('');
  const [analogyResult, setAnalogyResult] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [topNSim, setTopNSim]     = useState(8);

  const build = useCallback(() => {
    const c = buildCooccurrence(text, windowSize);
    setCooccur(c);
    const p = project2D(text);
    setPoints2D(p);
    return { c, p };
  }, [text, windowSize]);

  // Top-N most similar words to selected
  const topSimilar = useCallback((word) => {
    if (!cooccur || !word) return [];
    const idx = cooccur.vocab.indexOf(word);
    if (idx<0) return [];
    return cooccur.vocab
      .filter(w=>w!==word)
      .map(w => ({ word:w, sim: rowSimilarity(cooccur.mat, cooccur.vocab, word, w)||0 }))
      .sort((a,b)=>b.sim-a.sim)
      .slice(0, topNSim);
  }, [cooccur, topNSim]);

  const similar = selectedWord ? topSimilar(selectedWord) : [];

  // Pairwise similarity matrix (top 8 words by frequency)
  const matWords = cooccur ? cooccur.vocab.slice(0,8) : [];
  const simMat   = matWords.map(w1 => matWords.map(w2 =>
    w1===w2 ? 1 : (rowSimilarity(cooccur?.mat||[], cooccur?.vocab||[], w1, w2)||0)
  ));

  const colorForSim = v => {
    if (v>=0.8) return '#22c55e';
    if (v>=0.5) return '#eab308';
    if (v>=0.2) return '#f97316';
    return '#3f3f46';
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="CORPUS FOR EMBEDDINGS" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px',lineHeight:1.7}}>
              Embeddings are learned from word co-occurrence patterns. More text = better representations. The co-occurrence matrix captures which words appear near each other within a context window.
            </div>
            <textarea value={text} onChange={e=>{setText(e.target.value);setCooccur(null);setPoints2D([]);}} style={{width:'100%',minHeight:'140px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap',alignItems:'center'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>setText(t=>t+' '+SAMPLE_TEXTS[key])} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key} +</button>
              ))}
              <button onClick={()=>{setText(Object.values(SAMPLE_TEXTS).join(' '));setCooccur(null);setPoints2D([]);}} style={{padding:'5px 12px',border:`1px solid ${ACCENT}`,background:ACCENT+'18',color:ACCENT,fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>ALL SAMPLES</button>
            </div>
          </Panel>

          <Panel title="CONTEXT WINDOW SIZE" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              The context window determines which words count as "neighbors." Larger windows capture more topical similarity (documents about the same topic). Smaller windows capture more syntactic/functional similarity (words with the same grammatical role).
            </div>
            <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'14px',flexWrap:'wrap'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>WINDOW:</span>
              {[1,2,3,5,8].map(w=>(
                <button key={w} onClick={()=>setWindowSize(w)} style={{padding:'6px 14px',border:`1px solid ${windowSize===w?ACCENT:'var(--b0)'}`,background:windowSize===w?ACCENT+'18':'var(--raised)',color:windowSize===w?ACCENT:'var(--t2)',fontSize:'11px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:windowSize===w?700:400}}>±{w}</button>
              ))}
              <Btn onClick={()=>{build();setTab('cooccur');}} color={ACCENT}>BUILD EMBEDDINGS →</Btn>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
              {[
                {w:'±1',effect:'Syntactic',example:'"fast" ≈ "quick" (adjective)',color:'var(--cyan)'},
                {w:'±2',effect:'Syntactic+',example:'"run" ≈ "ran" (same verb family)',color:'var(--green)'},
                {w:'±3',effect:'Balanced',example:'"AI" ≈ "ML" (related concepts)',color:ACCENT},
                {w:'±5',effect:'Semantic',example:'"king" ≈ "royal" (topic overlap)',color:'var(--gold)'},
                {w:'±8',effect:'Topical',example:'"tech" ≈ "silicon" (document topic)',color:'var(--orange)'},
              ].map(({w,effect,example,color})=>(
                <div key={w} style={{padding:'10px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px',textAlign:'center'}}>
                  <div style={{fontSize:'12px',fontWeight:700,color,fontFamily:'JetBrains Mono',marginBottom:'4px'}}>{w}</div>
                  <div style={{fontSize:'10px',fontWeight:600,color:'var(--t2)',marginBottom:'4px'}}>{effect}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',fontStyle:'italic'}}>{example}</div>
                </div>
              ))}
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
            {[
              ['Corpus tokens',tokenizeWords(text).length,ACCENT],
              ['Content words',removeStopWords(tokenizeWords(text)).length,'var(--green)'],
              ['Unique vocab',new Set(removeStopWords(tokenizeWords(text))).size,'var(--cyan)'],
              ['Matrix dims',`${Math.min(40,new Set(removeStopWords(tokenizeWords(text))).size)}×${Math.min(40,new Set(removeStopWords(tokenizeWords(text))).size)}`,'var(--gold)'],
            ].map(([l,v,c])=>(
              <div key={l} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${c}22`,borderRadius:'3px',textAlign:'center'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>{String(l).toUpperCase()}</div>
                <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color:c}}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CO-OCCURRENCE ── */}
      {tab==='cooccur' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!cooccur && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={build} color={ACCENT}>BUILD CO-OCCURRENCE MATRIX →</Btn></div>}
          {cooccur && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <MC label="Vocabulary" value={cooccur.vocab.length} color={ACCENT}/>
                <MC label="Matrix size" value={`${cooccur.vocab.length}²`} sub="entries" color="var(--green)"/>
                <MC label="Window size" value={`±${windowSize}`} color="var(--cyan)"/>
                <MC label="Non-zero" value={cooccur.mat.flat().filter(v=>v>0).length} sub="entries" color="var(--gold)"/>
              </div>

              <Panel title="CO-OCCURRENCE MATRIX (top 12×12)" accent={ACCENT}>
                <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.65}}>
                  Matrix[i][j] = number of times word_i and word_j appear within ±{windowSize} words of each other. This raw matrix is the basis for all count-based embeddings (LSA, GloVe, PPMI).
                </div>
                <div style={{overflowX:'auto'}}>
                  {(()=>{
                    const words = cooccur.vocab.slice(0,12);
                    const maxVal = Math.max(...words.map(w1=>words.map(w2=>{
                      const i1=cooccur.vocab.indexOf(w1),i2=cooccur.vocab.indexOf(w2);
                      return cooccur.mat[i1]?.[i2]||0;
                    }).reduce((a,b)=>Math.max(a,b),0)),1);
                    return (
                      <table style={{borderCollapse:'collapse',fontSize:'9px',fontFamily:'JetBrains Mono'}}>
                        <thead><tr>
                          <th style={{padding:'5px 8px',color:'var(--t3)',fontSize:'8px'}}>—</th>
                          {words.map(w=><th key={w} style={{padding:'5px 8px',color:ACCENT,fontSize:'8px',textAlign:'center',maxWidth:'60px'}}>{w}</th>)}
                        </tr></thead>
                        <tbody>
                          {words.map((w1,i1)=>(
                            <tr key={w1}>
                              <td style={{padding:'5px 8px',color:ACCENT,fontWeight:700,fontSize:'8px',whiteSpace:'nowrap'}}>{w1}</td>
                              {words.map((w2,i2)=>{
                                const idx1=cooccur.vocab.indexOf(w1),idx2=cooccur.vocab.indexOf(w2);
                                const val = cooccur.mat[idx1]?.[idx2]||0;
                                const intensity = val/maxVal;
                                return (
                                  <td key={w2} style={{padding:'5px 8px',textAlign:'center',background:val>0?`rgba(244,114,182,${intensity*0.7})`:'transparent',color:val>0?'var(--t1)':'var(--t3)',fontWeight:val>maxVal*0.5?700:400,borderRadius:'2px'}} title={`${w1}↔${w2}: ${val}`}>
                                    {val||'·'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </Panel>

              <Panel title="TOP CO-OCCURRING PAIRS" accent={ACCENT}>
                {(()=>{
                  const pairs=[];
                  cooccur.vocab.forEach((w1,i)=>{
                    cooccur.vocab.forEach((w2,j)=>{
                      if(i<j&&cooccur.mat[i][j]>0) pairs.push({pair:`${w1} ↔ ${w2}`,count:cooccur.mat[i][j]});
                    });
                  });
                  const top=pairs.sort((a,b)=>b.count-a.count).slice(0,16);
                  return (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={top} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                        <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}}/>
                        <YAxis type="category" dataKey="pair" stroke="var(--t3)" tick={{fontSize:8}} width={130}/>
                        <Tooltip/>
                        <Bar dataKey="count" radius={[0,2,2,0]}>
                          {top.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Panel>

              <Panel title="FROM RAW COUNTS TO EMBEDDINGS — PPMI TRANSFORMATION" accent="var(--violet)">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[
                    {step:'1. Raw Count','color':'var(--cyan)','formula':'C(w,ctx)','desc':'Direct co-occurrence count. Biased toward frequent words: "the" and "cat" co-occur often just because "the" is everywhere.'},
                    {step:'2. PMI','color':'var(--gold)','formula':'log(P(w,ctx)/P(w)P(ctx))','desc':'Pointwise Mutual Information. Normalizes for word frequency. Negative values for words that avoid each other. Unbounded.'},
                    {step:'3. PPMI','color':'var(--green)','formula':'max(PMI, 0)','desc':'Positive PMI clips negatives to 0. Standard preprocessing for LSA. Used in GloVe loss function. Better than raw counts.'},
                    {step:'4. SVD/LSA','color':'var(--violet)','formula':'M = UΣVᵀ → U[:,:k]','desc':'Singular Value Decomposition reduces PPMI matrix to k dimensions. k=200-500. Captures latent semantic structure. Slow for large vocab.'},
                    {step:'5. Neural (W2V)','color':ACCENT,'formula':'argmin Σ -log σ(vw·vc)','desc':'Word2Vec: train neural net to predict context from word (or vice versa). Backprop learns word vectors directly. Faster and better than SVD.'},
                    {step:'6. GloVe','color':'var(--orange)','formula':'argmin Σ f(Cᵢⱼ)(wᵢᵀw̃ⱼ+bᵢ+b̃ⱼ−logCᵢⱼ)²','desc':'Combines count matrix (like LSA) with neural training (like W2V). Factorizes log co-occurrence matrix with weighting. Global context.'},
                  ].map(({step,color,formula,desc})=>(
                    <div key={step} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{fontSize:'10px',fontWeight:700,color,marginBottom:'4px'}}>{step}</div>
                      <div style={{padding:'4px 7px',background:'var(--deep)',borderRadius:'2px',fontFamily:'JetBrains Mono',fontSize:'9px',color:'var(--code)',marginBottom:'7px'}}>{formula}</div>
                      <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── 2D PROJECTION ── */}
      {tab==='project' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!points2D.length && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={()=>{build();}} color={ACCENT}>BUILD PROJECTION →</Btn></div>}
          {points2D.length > 0 && (
            <>
              <Panel title="2D WORD EMBEDDING PROJECTION" accent={ACCENT}>
                <div style={{display:'flex',gap:'10px',alignItems:'center',marginBottom:'12px',flexWrap:'wrap'}}>
                  <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',color:'var(--t2)',cursor:'pointer'}}>
                    <input type="checkbox" checked={showLabels} onChange={e=>setShowLabels(e.target.checked)} style={{accentColor:ACCENT}}/>
                    Show word labels
                  </label>
                  <Tag>{points2D.length} words projected</Tag>
                  <Tag color="var(--green)">window ±{windowSize}</Tag>
                  <span style={{fontSize:'10px',color:'var(--t3)'}}>Hover points to inspect. Nearby words = similar context patterns.</span>
                </div>
                <ResponsiveContainer width="100%" height={360}>
                  <ScatterChart margin={{top:20,right:20,bottom:20,left:20}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" opacity={0.4}/>
                    <XAxis dataKey="x" type="number" domain={['auto','auto']} stroke="var(--t3)" tick={{fontSize:8}} name="dim-1"/>
                    <YAxis dataKey="y" type="number" domain={['auto','auto']} stroke="var(--t3)" tick={{fontSize:8}} name="dim-2"/>
                    <Tooltip cursor={{strokeDasharray:'3 3'}} content={({active,payload})=>{
                      if (!active||!payload?.length) return null;
                      const d=payload[0]?.payload;
                      return (
                        <div style={{background:'var(--raised)',border:`1px solid ${ACCENT}55`,padding:'8px 12px',borderRadius:'3px',fontSize:'11px'}}>
                          <div style={{color:ACCENT,fontWeight:700,fontFamily:'JetBrains Mono',marginBottom:'4px'}}>{d?.word}</div>
                          <div style={{color:'var(--t3)'}}>x: {d?.x?.toFixed(4)}</div>
                          <div style={{color:'var(--t3)'}}>y: {d?.y?.toFixed(4)}</div>
                          <div style={{color:'var(--t3)'}}>freq: {d?.freq}</div>
                        </div>
                      );
                    }}/>
                    <Scatter data={points2D} shape={({cx,cy,payload})=>(
                      <g>
                        <circle cx={cx} cy={cy} r={Math.max(3,Math.min(8,Math.sqrt(payload.freq||1)*1.5))} fill={ACCENT} opacity={0.75} stroke={ACCENT} strokeWidth={1}/>
                        {showLabels&&<text x={cx+6} y={cy-6} fontSize={9} fill="var(--t2)" fontFamily="JetBrains Mono">{payload.word}</text>}
                      </g>
                    )}/>
                  </ScatterChart>
                </ResponsiveContainer>
                <InfoBox color={ACCENT} icon="◫" style={{marginTop:'8px'}}>
                  This 2D projection uses co-occurrence statistics — not true Word2Vec. Real W2V/GloVe embeddings are 100-300 dimensional; visualized with t-SNE or UMAP. The relative positions here reflect co-occurrence patterns, not full semantic geometry.
                </InfoBox>
              </Panel>

              <Panel title="DIMENSIONALITY REDUCTION METHODS" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[
                    {name:'PCA',color:'var(--cyan)',desc:'Linear. Maximizes variance. Fast O(n²). Preserves global structure. Best for initial exploration. Loses local neighborhood structure in high dimensions.'},
                    {name:'t-SNE',color:'var(--gold)',desc:'Non-linear. Preserves local neighborhoods. Good for visualizing clusters. Slow O(n²). Non-deterministic. Perplexity hyperparameter critical. Most common for embeddings.'},
                    {name:'UMAP',color:'var(--green)',desc:'Faster than t-SNE. Preserves more global structure. Deterministic with fixed seed. Better for downstream tasks. Used by BERTopic, scRNA-seq. Best overall choice.'},
                    {name:'Our method',color:ACCENT,desc:'Co-occurrence row normalization + inner product projection. Fast, interpretable. Not as semantically meaningful as true W2V+UMAP but runs entirely in browser with zero ML.'},
                    {name:'LDA (supervised)',color:'var(--violet)',desc:'Linear Discriminant Analysis. Uses class labels. Maximizes class separation. Useful when you have labeled embeddings to visualize.'},
                    {name:'Autoencoder',color:'var(--orange)',desc:'Neural encoder-decoder learns compressed latent representations. Can be non-linear. Bottleneck layer gives embeddings. Used for anomaly detection + visualization.'},
                  ].map(({name,color,desc})=>(
                    <div key={name} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                      <div style={{fontSize:'12px',fontWeight:700,color,marginBottom:'6px',fontFamily:'JetBrains Mono'}}>{name}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── SIMILARITY ── */}
      {tab==='similarity' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!cooccur && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={build} color={ACCENT}>BUILD EMBEDDINGS FIRST →</Btn></div>}
          {cooccur && (
            <>
              <Panel title="WORD SIMILARITY EXPLORER" accent={ACCENT}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>SELECT WORD (find similar)</div>
                    <select value={selectedWord} onChange={e=>setSelectedWord(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}>
                      <option value="">— pick a word —</option>
                      {cooccur.vocab.map(w=><option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.1em'}}>TOP-K SIMILAR: {topNSim}</div>
                    <input type="range" min={3} max={15} value={topNSim} onChange={e=>setTopNSim(+e.target.value)} style={{width:'100%',accentColor:ACCENT,marginTop:'12px'}}/>
                  </div>
                </div>

                {selectedWord && similar.length > 0 && (
                  <>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>MOST SIMILAR TO "{selectedWord}"</div>
                    <div style={{display:'flex',flexDirection:'column',gap:'5px',marginBottom:'12px'}}>
                      {similar.map(({word,sim},i)=>(
                        <div key={word} style={{display:'flex',alignItems:'center',gap:'10px',padding:'7px 12px',background:'var(--raised)',borderRadius:'3px',border:'1px solid var(--b0)'}}>
                          <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:'22px'}}>#{i+1}</span>
                          <span style={{fontSize:'12px',color:'var(--t1)',fontFamily:'JetBrains Mono',flex:1,fontWeight:i===0?700:400}}>{word}</span>
                          <div style={{width:'140px',height:'6px',background:'var(--deep)',borderRadius:'3px'}}>
                            <div style={{height:'100%',width:`${Math.max(0,sim)*100}%`,background:P[i%P.length],borderRadius:'3px'}}/>
                          </div>
                          <span style={{fontSize:'11px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700,minWidth:'48px',textAlign:'right'}}>{sim.toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={similar}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="word" stroke="var(--t3)" tick={{fontSize:9}} angle={-15} textAnchor="end" height={44} interval={0}/>
                        <YAxis stroke="var(--t3)" tick={{fontSize:9}} domain={[0,1]}/>
                        <Tooltip/>
                        <Bar dataKey="sim" name="cosine sim" radius={[2,2,0,0]}>
                          {similar.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </Panel>

              {/* Direct pair comparison */}
              <Panel title="PAIRWISE SIMILARITY" accent={ACCENT}>
                <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap',marginBottom:'12px'}}>
                  <div style={{flex:1,minWidth:'140px'}}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>WORD A</div>
                    <select value={simWord1} onChange={e=>setSimWord1(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}>
                      <option value="">—</option>
                      {cooccur.vocab.map(w=><option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <div style={{flex:1,minWidth:'140px'}}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>WORD B</div>
                    <select value={simWord2} onChange={e=>setSimWord2(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}>
                      <option value="">—</option>
                      {cooccur.vocab.map(w=><option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <Btn onClick={()=>{if(simWord1&&simWord2){const s=rowSimilarity(cooccur.mat,cooccur.vocab,simWord1,simWord2);setSimResult(s);}}} color={ACCENT}>COMPARE</Btn>
                </div>
                {simResult!==null && simWord1 && simWord2 && (
                  <div style={{padding:'20px',background:'var(--deep)',border:`1px solid ${ACCENT}44`,borderRadius:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'13px',color:'var(--t2)',marginBottom:'8px'}}>cos("<span style={{color:ACCENT}}>{simWord1}</span>", "<span style={{color:ACCENT}}>{simWord2}</span>")</div>
                    <div style={{fontSize:'42px',fontWeight:800,fontFamily:'Syne',color:simResult>0.5?'var(--green)':simResult>0.2?'var(--gold)':'var(--t3)'}}>{simResult?.toFixed(4)??'—'}</div>
                    <div style={{fontSize:'11px',color:'var(--t3)',marginTop:'6px'}}>
                      {simResult>0.7?'Very similar context patterns':simResult>0.4?'Moderately similar':simResult>0.1?'Weakly similar':'Very different contexts'}
                    </div>
                  </div>
                )}
              </Panel>

              {/* Similarity matrix */}
              <Panel title={`PAIRWISE SIMILARITY MATRIX (top ${matWords.length} words)`} accent={ACCENT}>
                <div style={{overflowX:'auto'}}>
                  <table style={{borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                    <thead><tr>
                      <th style={{padding:'6px 10px',color:'var(--t3)',fontSize:'8px'}}>—</th>
                      {matWords.map(w=><th key={w} style={{padding:'6px 10px',color:ACCENT,fontSize:'8px',textAlign:'center'}}>{w}</th>)}
                    </tr></thead>
                    <tbody>
                      {matWords.map((w1,i)=>(
                        <tr key={w1}>
                          <td style={{padding:'6px 10px',color:ACCENT,fontWeight:700,fontSize:'8px',whiteSpace:'nowrap'}}>{w1}</td>
                          {simMat[i].map((val,j)=>(
                            <td key={j} style={{padding:'6px 10px',textAlign:'center',background:i===j?'var(--raised)':`rgba(244,114,182,${val*0.5})`,color:i===j?'var(--t3)':val>0.3?'var(--t1)':'var(--t3)',fontWeight:val>0.4&&i!==j?700:400,borderRadius:'2px',cursor:'pointer'}} title={`${w1} ↔ ${matWords[j]}: ${val.toFixed(3)}`}>
                              {i===j?'1.00':val.toFixed(2)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── ANALOGIES ── */}
      {tab==='analogies' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="WORD ANALOGIES — a : b :: c : ?" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              Word2Vec's famous property: vec("king") − vec("man") + vec("woman") ≈ vec("queen"). We approximate this with our 2D co-occurrence projections. True analogies require much larger corpora and proper Word2Vec training.
            </div>
            {!points2D.length && <Btn onClick={build} color="var(--violet)">BUILD EMBEDDINGS FIRST →</Btn>}
            {points2D.length > 0 && (
              <>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'8px',marginBottom:'12px',alignItems:'flex-end'}}>
                  {[
                    {label:'A (source)',val:analogyA,set:setAnalogyA},
                    {label:'B (target)',val:analogyB,set:setAnalogyB},
                    {label:'C (query)',val:analogyC,set:setAnalogyC},
                  ].map(({label,val,set})=>(
                    <div key={label}>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>{label}</div>
                      <select value={val} onChange={e=>set(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}>
                        <option value="">—</option>
                        {points2D.map(p=><option key={p.word} value={p.word}>{p.word}</option>)}
                      </select>
                    </div>
                  ))}
                  <Btn onClick={()=>{if(analogyA&&analogyB&&analogyC){setAnalogyResult(solveAnalogy(points2D,analogyA,analogyB,analogyC));}}} color="var(--violet)">SOLVE →</Btn>
                </div>

                <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'14px'}}>
                  {['machine learning artificial','learning machine deep','intelligence model language','quantum computing classical','data training model'].map(trio=>{
                    const [a,b,c]=trio.split(' ');
                    const hasAll = [a,b,c].every(w=>points2D.find(p=>p.word===w));
                    return hasAll&&(
                      <button key={trio} onClick={()=>{setAnalogyA(a);setAnalogyB(b);setAnalogyC(c);setAnalogyResult(solveAnalogy(points2D,a,b,c));}} style={{padding:'4px 10px',border:'1px solid var(--b0)',background:'var(--raised)',color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                        onMouseEnter={e=>e.currentTarget.style.color='var(--violet)'} onMouseLeave={e=>e.currentTarget.style.color='var(--t3)'}>{a}:{b}::{c}:?</button>
                    );
                  })}
                </div>

                {analogyResult && analogyA && analogyB && analogyC && (
                  <div style={{padding:'16px',background:'var(--deep)',border:'1px solid var(--violet)44',borderRadius:'4px'}}>
                    <div style={{fontSize:'13px',color:'var(--t2)',marginBottom:'12px',fontFamily:'JetBrains Mono'}}>
                      <span style={{color:'var(--cyan)'}}>{analogyA}</span> : <span style={{color:'var(--green)'}}>{analogyB}</span> :: <span style={{color:'var(--gold)'}}>{analogyC}</span> : <span style={{color:'var(--violet)',fontWeight:700}}>{analogyResult[0]?.word}?</span>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
                      {analogyResult.map(({word,dist},i)=>(
                        <div key={word} style={{display:'flex',alignItems:'center',gap:'10px',padding:'6px 10px',background:'var(--raised)',borderRadius:'3px',border:`1px solid ${i===0?'var(--violet)':'var(--b0)'}`}}>
                          <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:'22px'}}>#{i+1}</span>
                          <span style={{fontSize:'12px',color:i===0?'var(--violet)':'var(--t1)',fontFamily:'JetBrains Mono',fontWeight:i===0?700:400,flex:1}}>{word}</span>
                          <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>dist: {dist.toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </Panel>

          <Panel title="FAMOUS WORD2VEC ANALOGIES" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
              {[
                ['king − man + woman ≈ queen','Royalty + gender'],
                ['Paris − France + Italy ≈ Rome','Capitals'],
                ['walking − walk + swim ≈ swimming','Verb inflection'],
                ['bigger − big + cold ≈ colder','Comparatives'],
                ['king − queen + woman ≈ man','Gender'],
                ['Paris − France + Germany ≈ Berlin','Geography'],
                ['Einstein − physicist + mathematician ≈ Gauss','Profession'],
                ['Windows − Microsoft + Google ≈ Android','Products'],
              ].map(([analogy,category])=>(
                <div key={analogy} style={{padding:'8px 12px',background:'var(--raised)',border:'1px solid var(--violet)22',borderRadius:'3px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:'10px',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{analogy}</span>
                  <Tag color="var(--violet)">{category}</Tag>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── TRAINING ── */}
      {tab==='training' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="WORD2VEC TRAINING — SKIP-GRAM & CBOW" accent="var(--green)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'14px'}}>
              <div style={{padding:'14px',background:'var(--raised)',border:'1px solid var(--green)44',borderRadius:'4px'}}>
                <div style={{fontSize:'12px',fontWeight:700,color:'var(--green)',marginBottom:'8px'}}>SKIP-GRAM</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.7,marginBottom:'10px'}}>
                  Predict context words from center word. "The quick brown fox" with center="quick": predict {"{the, brown}"}. Works well with small data, rare words. Slower to train. Default in original W2V.
                </div>
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'10px',textAlign:'center'}}>
                  <div style={{color:'var(--t3)',marginBottom:'6px'}}>… The <span style={{color:'var(--t3)'}}>quick</span> brown …</div>
                  <div style={{color:'var(--green)',marginBottom:'4px'}}>↓ input: "quick"</div>
                  <div style={{color:'var(--t3)'}}>predict: ["the", "brown"]</div>
                </div>
              </div>
              <div style={{padding:'14px',background:'var(--raised)',border:'1px solid var(--cyan)44',borderRadius:'4px'}}>
                <div style={{fontSize:'12px',fontWeight:700,color:'var(--cyan)',marginBottom:'8px'}}>CBOW (Continuous Bag of Words)</div>
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.7,marginBottom:'10px'}}>
                  Predict center word from averaged context embeddings. Several times faster than Skip-gram. Better for frequent words, large datasets. Averages context → loses word order.
                </div>
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'10px',textAlign:'center'}}>
                  <div style={{color:'var(--t3)',marginBottom:'6px'}}>… <span style={{color:'var(--cyan)'}}>The</span> _?_ <span style={{color:'var(--cyan)'}}>brown</span> …</div>
                  <div style={{color:'var(--cyan)',marginBottom:'4px'}}>↓ input: ["the", "brown"]</div>
                  <div style={{color:'var(--t3)'}}>predict: "quick"</div>
                </div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {trick:'Negative Sampling',color:ACCENT,desc:'Instead of full softmax over V words, sample k negative examples (k=5-20). Trains to separate "quick" from random noise words. Turns classification into k+1 binary problems. Key speedup.'},
                {trick:'Hierarchical Softmax',color:'var(--violet)',desc:'Huffman tree over vocabulary. Frequent words near root (shorter path). O(log V) instead of O(V) per step. No negative sampling needed. Used in original W2V for large vocab.'},
                {trick:'Subsampling',color:'var(--gold)',desc:'Discard frequent words with probability p(w)=1−√(t/f(w)). Reduces "the","a","of" dominance. Makes training faster and improves quality of rare word vectors.'},
                {trick:'Window Position',color:'var(--orange)',desc:'In Word2Vec, context words closer to the center are sampled more often. Effectively weights nearby context more. This is why W2V captures syntactic relationships better with small windows.'},
                {trick:'Dim Selection',color:'var(--green)',desc:'d=50: fast, good for freq tasks. d=100: balanced. d=300: original W2V on Google News (3B words). d=768: BERT. Higher d better but more compute. Optimal d scales with corpus size.'},
                {trick:'Training Corpus',color:'var(--cyan)',desc:'Original Google News W2V: 100B words → 3M vocab, 300d. GloVe 840B: Common Crawl, 2.2M vocab, 300d. FastText: Wikipedia+news, 1M vocab including subwords. Quality depends heavily on corpus domain.'},
              ].map(({trick,color,desc})=>(
                <div key={trick} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'3px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'6px'}}>{trick}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── MODELS ── */}
      {tab==='models' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="EMBEDDING MODELS — COMPARISON" accent={ACCENT}>
            <div style={{overflowX:'auto',marginBottom:'14px'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {['Model','Year','Dim','Vocab','Method','OOV','Contextual'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',color:'var(--t3)',fontSize:'9px',letterSpacing:'.08em',borderBottom:'1px solid var(--b0)'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[
                    {name:'Word2Vec SG',color:'var(--cyan)',  year:'2013',dim:'50-300',vocab:'1M+', method:'Neural SGNS',     oov:'No',  ctx:'No'},
                    {name:'GloVe',     color:'var(--green)', year:'2014',dim:'50-300',vocab:'2.2M',method:'Matrix factor.',  oov:'No',  ctx:'No'},
                    {name:'FastText',  color:'var(--gold)',   year:'2016',dim:'100-300',vocab:'2M+',method:'Char n-grams+W2V',oov:'Yes', ctx:'No'},
                    {name:'ELMo',      color:'var(--violet)', year:'2018',dim:'1024',  vocab:'∞',  method:'BiLSTM LM',       oov:'Yes', ctx:'Yes'},
                    {name:'BERT',      color:'var(--orange)', year:'2018',dim:'768',   vocab:'30K BPE',method:'Transformer MLM',oov:'Yes',ctx:'Yes'},
                    {name:'RoBERTa',   color:'var(--red)',    year:'2019',dim:'768',   vocab:'50K BPE',method:'BERT improved', oov:'Yes',ctx:'Yes'},
                    {name:'GPT-3',     color:ACCENT,          year:'2020',dim:'12288', vocab:'50K BPE',method:'Causal Transf.', oov:'Yes',ctx:'Yes'},
                    {name:'LLaMA-2',   color:'#34d399',       year:'2023',dim:'4096',  vocab:'32K SP', method:'Causal Transf.', oov:'Yes',ctx:'Yes'},
                  ].map(({name,color,year,dim,vocab,method,oov,ctx})=>(
                    <tr key={name} style={{borderBottom:'1px solid var(--b0)'}}>
                      <td style={{padding:'8px 12px',color,fontWeight:700,fontFamily:'JetBrains Mono'}}>{name}</td>
                      <td style={{padding:'8px 12px',color:'var(--t3)'}}>{year}</td>
                      <td style={{padding:'8px 12px',color:'var(--cyan)',fontFamily:'JetBrains Mono'}}>{dim}</td>
                      <td style={{padding:'8px 12px',color:'var(--t3)'}}>{vocab}</td>
                      <td style={{padding:'8px 12px',color:'var(--t3)',fontSize:'10px'}}>{method}</td>
                      <td style={{padding:'8px 12px',textAlign:'center'}}>{oov==='Yes'?<Tag color="var(--green)">✓</Tag>:<Tag color="var(--red)">✗</Tag>}</td>
                      <td style={{padding:'8px 12px',textAlign:'center'}}>{ctx==='Yes'?<Tag color="var(--green)">✓</Tag>:<Tag color="var(--red)">✗</Tag>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <InfoBox color={ACCENT} icon="◫">
              <strong>OOV</strong>: handles out-of-vocabulary words (via subwords). <strong>Contextual</strong>: same word gets different vectors in different sentences ("bank" in "river bank" vs "bank account"). Non-contextual models assign a single fixed vector per word.
            </InfoBox>
          </Panel>

          <Panel title="SENTENCE EMBEDDINGS" accent={ACCENT}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {name:'Avg Word Vectors',color:'var(--t3)',desc:'Average all word vectors in sentence. Fast. Loses word order and sentence structure. Surprisingly competitive for short texts. Baseline for sentence similarity.'},
                {name:'Doc2Vec',color:'var(--cyan)',desc:'Paragraph Vector (Le & Mikolov 2014). Adds document embedding trained jointly with word vectors. Captures document-level semantics. Used for document classification.'},
                {name:'InferSent',color:'var(--gold)',desc:'Facebook 2017. Bi-LSTM trained on NLI (natural language inference). Learns to encode sentence meaning. Better than avg W2V. Good generalization.'},
                {name:'Universal Sentence Encoder',color:'var(--orange)',desc:'Google 2018. Transformer+DAN dual architecture. 512d. Optimized for semantic similarity, clustering, classification. Available on TF Hub.'},
                {name:'Sentence-BERT (SBERT)',color:'var(--violet)',desc:'Bi-encoder BERT with siamese network. Contrastive loss on NLI+STS. Cosine sim works properly. 100× faster than BERT cross-encoder for similarity. sentence-transformers library.'},
                {name:'text-embedding-ada-002',color:ACCENT,desc:'OpenAI 2022. 1536d. Trained on massive data. Best overall on MTEB benchmark. $0.0001/1K tokens. Used for semantic search, RAG, clustering at scale.'},
              ].map(({name,color,desc})=>(
                <div key={name} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color,marginBottom:'7px'}}>{name}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Distributional Hypothesis',color:ACCENT,desc:'"You shall know a word by the company it keeps" (Firth, 1957). Words that appear in similar contexts have similar meanings. This is the entire theoretical basis for word embeddings. It explains why "bank" can mean two things — it appears in different contexts.'},
              {title:'Geometry of Meaning',color:'var(--violet)',desc:'Word2Vec encodes semantic relationships as linear transformations in vector space: gender offset vec(woman)−vec(man) applies to king→queen, uncle→aunt, waiter→waitress. Country→capital offset consistent. Tense, plurality encoded as directions.'},
              {title:'Bias in Embeddings',color:'var(--red)',desc:'Embeddings absorb societal biases from training data. "doctor"−"nurse" ≈ "man"−"woman". Debiasing: Bolukbasi et al. project out gender subspace. Hard debiasing: zero gender component for profession words. But bias can be subtle and reappear.'},
              {title:'Polysemy Problem',color:'var(--orange)',desc:'Static embeddings (W2V, GloVe) assign one vector per word. "bank" (river/finance), "bat" (animal/baseball), "spring" (season/coil). Solution: ELMo, BERT generate different vectors per context. Sense disambiguation becomes implicit.'},
              {title:'MTEB Benchmark',color:'var(--green)',desc:'Massive Text Embedding Benchmark: 56 datasets across 8 task categories (classification, clustering, retrieval, STS, etc.). Standard for evaluating sentence/document embeddings. text-embedding-3-large tops the leaderboard. Hugging Face: mteb/mteb.'},
              {title:'RAG & Vector Databases',color:'var(--cyan)',desc:'Retrieval-Augmented Generation: encode documents → store in vector DB (Pinecone, Weaviate, Chroma, FAISS) → encode query → ANN search → retrieve top-k → feed to LLM as context. Solves LLM knowledge cutoff. Knowledge can be updated without retraining.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
